import { useEffect, useState, useRef } from "react";
import { useCountries } from "store/countries/countries.store";
import { apiV2 } from "api/firebase";
import { IState, ICity } from "@evennt-tech/api-v2-core";

interface UseAddressLocationsProps {
  venueCountryId: string;
  venueState: string;
  setValue: (name: any, value: any, options?: object) => void;
  shouldResetOnCountryChange?: boolean;
}

export const useAddressLocations = ({
  venueCountryId,
  venueState,
  setValue,
  shouldResetOnCountryChange = true,
}: UseAddressLocationsProps) => {
  const { countries, fetchCountries } = useCountries();
  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  const prevCountryId = useRef<string | undefined>(venueCountryId);
  const prevState = useRef<string | undefined>(venueState);

  useEffect(() => {
    fetchCountries(true);
  }, [fetchCountries]);

  useEffect(() => {
    if (!venueCountryId) return;

    const fetchStatesAndCities = async () => {
      setIsLoadingLocations(true);
      const country = countries.find((c) => c.id === venueCountryId);
      const countryCode = country?.code2;

      if (!countryCode) {
        setIsLoadingLocations(false);
        return;
      }

      try {
        if (
          shouldResetOnCountryChange &&
          prevCountryId.current &&
          prevCountryId.current !== venueCountryId
        ) {
          setValue("venueState", "");
          setValue("venueCity", "");
        }
        prevCountryId.current = venueCountryId;

        const statesData = await apiV2.country.getStatesByCountry({
          countryIso2: countryCode,
        });

        setStates(statesData);

        if (statesData.length === 0) {
          const citiesData = await apiV2.country.getCitiesByCountry({
            countryIso2: countryCode,
          });
          setCities(citiesData);
        } else {
          setCities([]);
        }
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchStatesAndCities();
  }, [venueCountryId, countries, setValue, shouldResetOnCountryChange]);

  useEffect(() => {
    if (!venueState || !venueCountryId) return;

    const fetchCitiesByState = async () => {
      setIsLoadingLocations(true);
      const country = countries.find((c) => c.id === venueCountryId);
      const selectedStateObj = states.find((s) => s.name === venueState);

      if (!country?.code2 || !selectedStateObj?.iso2) {
        setIsLoadingLocations(false);
        return;
      }

      try {
        if (
          shouldResetOnCountryChange &&
          prevState.current &&
          prevState.current !== venueState
        ) {
          setValue("venueCity", "");
        }
        prevState.current = venueState;

        const data = await apiV2.country.getCitiesByState({
          countryIso2: country.code2,
          stateIso2: selectedStateObj.iso2,
        });

        setCities(data);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchCitiesByState();
  }, [
    venueState,
    venueCountryId,
    countries,
    states,
    setValue,
    shouldResetOnCountryChange,
  ]);

  return { countries, states, cities, isLoadingLocations };
};
